import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import {
  Badge,
  Card,
  Empty,
  Failure,
  Field,
  Loading,
  Modal,
  Notice,
} from '../components/ui.js';
import {
  EVENT_KIND_LABELS,
  PROBLEM_LABELS,
  WEEKDAYS,
  formatDateTime,
  formatDuration,
} from '../lib/format.js';

/* --------------------------------------------------------------- types */

interface Period {
  kind: 'lesson' | 'break';
  index: number;
  duration_minutes: number;
}

interface BoundaryRule {
  rule_id?: string;
  boundary_index: number;
  enabled: boolean;
  asset_id: string | null;
  max_duration_ms: number;
  truncate: boolean;
  label: string | null;
  offset_minutes: number;
  late_window_ms: number | null;
}

interface ExtraRule {
  rule_id?: string;
  enabled: boolean;
  kind: 'anthem' | 'custom';
  label: string;
  asset_id: string | null;
  max_duration_ms: number;
  truncate: boolean;
  anchor_type: 'shift_start' | 'boundary' | 'local_time';
  anchor_boundary_index: number | null;
  anchor_time: string | null;
  offset_minutes: number;
  run_on_holidays: boolean;
  ambiguous_time_preference: 'first' | 'second' | null;
}

interface Shift {
  shift_id?: string;
  name: string;
  weekdays: number[];
  start_time: string;
  ambiguous_time_preference: 'first' | 'second' | null;
  periods: Period[];
  boundary_rules: BoundaryRule[];
  extra_rules: ExtraRule[];
}

interface Template {
  id?: string;
  name: string;
  notes: string | null;
  shifts: Shift[];
}

interface AudioAsset {
  id: string;
  original_name: string;
  state: string;
  duration_ms: number | null;
}

interface Problem {
  code: string;
  severity: 'error' | 'warning';
  message: string;
  service_date?: string;
}

interface PreviewResult {
  from_date: string;
  to_date: string;
  device_count: number;
  blocking_device_count: number;
  total_events: number;
  can_publish: boolean;
  global_problems: Problem[];
  devices: Array<{
    device_id: string;
    device_name: string;
    school_name: string;
    timezone: string;
    template_name: string;
    assignment_source: string;
    event_count: number;
    asset_count: number;
    bytes_total: number;
    required_storage_bytes: number;
    first_events: Array<{ at: string; label: string; kind: string }>;
    suppressed_dates: Array<{ date: string; label: string }>;
    problems: Problem[];
    blocking: boolean;
  }>;
}

/* ---------------------------------------------------------------- page */

const EMPTY_TEMPLATE: Template = {
  name: '',
  notes: null,
  shifts: [makeShift('الصباحي', '07:30')],
};

function makeShift(name: string, startTime: string): Shift {
  const periods: Period[] = [
    { kind: 'lesson', index: 1, duration_minutes: 40 },
    { kind: 'break', index: 1, duration_minutes: 15 },
    { kind: 'lesson', index: 2, duration_minutes: 40 },
  ];
  return {
    name,
    weekdays: [0, 1, 2, 3, 4],
    start_time: startTime,
    ambiguous_time_preference: null,
    periods,
    boundary_rules: buildBoundaries(periods, []),
    extra_rules: [],
  };
}

/** Keep exactly periods.length + 1 boundary rules, preserving existing rule ids. */
function buildBoundaries(periods: Period[], existing: BoundaryRule[]): BoundaryRule[] {
  const out: BoundaryRule[] = [];
  for (let i = 0; i <= periods.length; i++) {
    const prev = existing.find((b) => b.boundary_index === i);
    out.push(
      prev ?? {
        boundary_index: i,
        enabled: true,
        asset_id: null,
        max_duration_ms: 5000,
        truncate: false,
        label: null,
        offset_minutes: 0,
        late_window_ms: null,
      },
    );
  }
  return out;
}

/** Mirror of the engine's labelling so the editor shows what will be generated. */
const ORDINALS = ['الأولى', 'الثانية', 'الثالثة', 'الرابعة', 'الخامسة', 'السادسة', 'السابعة', 'الثامنة', 'التاسعة', 'العاشرة'];
const ord = (n: number): string => ORDINALS[n - 1] ?? String(n);

function boundaryLabel(periods: Period[], i: number): string {
  if (periods.length === 0) return 'حد';
  if (i >= periods.length) {
    const last = periods[periods.length - 1]!;
    return last.kind === 'lesson' ? `نهاية الحصة ${ord(last.index)}` : `نهاية الاستراحة ${ord(last.index)}`;
  }
  const next = periods[i]!;
  const prev = i > 0 ? periods[i - 1] : undefined;
  const starts = next.kind === 'lesson' ? `بداية الحصة ${ord(next.index)}` : `بداية الاستراحة ${ord(next.index)}`;
  if (!prev) return starts;
  const ends = prev.kind === 'lesson' ? `نهاية الحصة ${ord(prev.index)}` : `نهاية الاستراحة ${ord(prev.index)}`;
  return `${ends} و${starts}`;
}

/** Local wall-clock time of each boundary, as minutes from the shift start. */
function boundaryTimes(shift: Shift): string[] {
  const [h, m] = shift.start_time.split(':').map(Number);
  let minutes = (h ?? 0) * 60 + (m ?? 0);
  const out: string[] = [fmt(minutes)];
  for (const p of shift.periods) {
    minutes += p.duration_minutes;
    out.push(fmt(minutes));
  }
  return out;

  function fmt(total: number): string {
    const day = ((total % 1440) + 1440) % 1440;
    return `${String(Math.floor(day / 60)).padStart(2, '0')}:${String(day % 60).padStart(2, '0')}`;
  }
}

export function ScheduleEditorPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [template, setTemplate] = useState<Template>(EMPTY_TEMPLATE);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [showPublish, setShowPublish] = useState(false);

  const loaded = useQuery({
    queryKey: ['schedule', id],
    queryFn: () => get<Template & { shifts: Shift[] }>(`/schedules/${id}`),
    enabled: !isNew,
  });

  const assets = useQuery({
    queryKey: ['audio', 'ready'],
    queryFn: () => get<{ items: AudioAsset[] }>('/audio?page_size=200&state=READY'),
  });

  useEffect(() => {
    if (loaded.data) setTemplate(loaded.data);
  }, [loaded.data]);

  const save = useMutation({
    mutationFn: (body: Template) =>
      isNew
        ? post<{ id: string }>('/schedules', stripIds(body))
        : put<{ id: string }>(`/schedules/${id}`, body),
    onSuccess: (res) => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['schedules'] });
      if (isNew && res?.id) navigate(`/schedules/${res.id}`, { replace: true });
      else void loaded.refetch();
    },
    onError: (e: Error) => setError(e.message),
  });

  const runPreview = useMutation({
    mutationFn: () => post<PreviewResult>(`/schedules/${id}/preview`, { horizon_days: 90 }),
    onSuccess: (res) => {
      setPreview(res);
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const publish = useMutation({
    mutationFn: (note: string) =>
      post<{ revision: number; device_count: number; total_events: number }>(
        `/schedules/${id}/publish`,
        { horizon_days: 90, confirm: true, note },
        // A retry of the same approval must not create a second revision (§10).
        `publish-${id}-${preview?.from_date}-${preview?.total_events}`,
      ),
    onSuccess: () => {
      setShowPublish(false);
      setPreview(null);
      void queryClient.invalidateQueries({ queryKey: ['deployments'] });
      navigate('/deployments');
    },
    onError: (e: Error) => setError(e.message),
  });

  const readyAssets = useMemo(
    () => (assets.data?.items ?? []).filter((a) => a.state === 'READY'),
    [assets.data],
  );

  if (!isNew && loaded.isLoading) return <Loading />;
  if (!isNew && loaded.error) return <Failure error={loaded.error} onRetry={() => void loaded.refetch()} />;

  const updateShift = (index: number, next: Shift): void =>
    setTemplate((t) => ({ ...t, shifts: t.shifts.map((s, i) => (i === index ? next : s)) }));

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card title={isNew ? 'قالب جدول جديد' : `تعديل: ${template.name}`}>
        <div className="grid cols-2">
          <Field label="اسم القالب">
            <input
              value={template.name}
              onChange={(e) => setTemplate((t) => ({ ...t, name: e.target.value }))}
            />
          </Field>
          <Field label="ملاحظات (اختياري)">
            <input
              value={template.notes ?? ''}
              onChange={(e) => setTemplate((t) => ({ ...t, notes: e.target.value || null }))}
            />
          </Field>
        </div>
        <Notice tone="info">
          الحد المشترك بين حصة واستراحة يولّد <strong>صوتًا واحدًا</strong>، لا جرس نهاية وبداية
          متطابقين. لا توجد حقول للمواد الدراسية أو المدرسين.
        </Notice>
      </Card>

      {template.shifts.map((shift, i) => (
        <ShiftEditor
          key={shift.shift_id ?? i}
          shift={shift}
          assets={readyAssets}
          onChange={(next) => updateShift(i, next)}
          onRemove={
            template.shifts.length > 1
              ? () => setTemplate((t) => ({ ...t, shifts: t.shifts.filter((_, x) => x !== i) }))
              : undefined
          }
        />
      ))}

      <div className="row" style={{ marginBottom: 16 }}>
        <button
          onClick={() =>
            setTemplate((t) => ({ ...t, shifts: [...t.shifts, makeShift('المسائي', '13:00')] }))
          }
          disabled={template.shifts.length >= 6}
        >
          إضافة دوام
        </button>
        <div style={{ flex: 1 }} />
        {can('schedule.manage') ? (
          <button
            className="primary"
            disabled={save.isPending || !template.name || template.shifts.length === 0}
            onClick={() => save.mutate(template)}
          >
            {save.isPending ? 'جارٍ الحفظ…' : 'حفظ القالب'}
          </button>
        ) : null}
        {!isNew ? (
          <button disabled={runPreview.isPending} onClick={() => runPreview.mutate()}>
            {runPreview.isPending ? 'جارٍ الحساب…' : 'معاينة النشر'}
          </button>
        ) : null}
      </div>

      {preview ? (
        <PreviewPanel
          preview={preview}
          canPublish={can('schedule.publish')}
          onPublish={() => setShowPublish(true)}
        />
      ) : null}

      {showPublish && preview ? (
        <PublishDialog
          preview={preview}
          busy={publish.isPending}
          onCancel={() => setShowPublish(false)}
          onConfirm={(note) => publish.mutate(note)}
        />
      ) : null}
    </>
  );
}

/** Strip stored rule ids when cloning into a brand-new template. */
function stripIds(t: Template): Template {
  return {
    ...t,
    shifts: t.shifts.map((s) => ({
      ...s,
      shift_id: undefined,
      boundary_rules: s.boundary_rules.map((b) => ({ ...b, rule_id: undefined })),
      extra_rules: s.extra_rules.map((r) => ({ ...r, rule_id: undefined })),
    })),
  };
}

/* ------------------------------------------------------------ shift UI */

function ShiftEditor({
  shift,
  assets,
  onChange,
  onRemove,
}: {
  shift: Shift;
  assets: AudioAsset[];
  onChange: (next: Shift) => void;
  onRemove?: () => void;
}): JSX.Element {
  const times = boundaryTimes(shift);

  const setPeriods = (periods: Period[]): void =>
    onChange({ ...shift, periods, boundary_rules: buildBoundaries(periods, shift.boundary_rules) });

  const addPeriod = (kind: 'lesson' | 'break'): void => {
    const index = shift.periods.filter((p) => p.kind === kind).length + 1;
    setPeriods([...shift.periods, { kind, index, duration_minutes: kind === 'lesson' ? 40 : 15 }]);
  };

  return (
    <Card
      title={`دوام: ${shift.name}`}
      subtitle={`${shift.periods.length} فترة · ${shift.boundary_rules.length} حد`}
      actions={onRemove ? <button className="ghost" onClick={onRemove}>حذف الدوام</button> : null}
    >
      <div className="grid cols-3">
        <Field label="اسم الدوام">
          <input value={shift.name} onChange={(e) => onChange({ ...shift, name: e.target.value })} />
        </Field>
        <Field label="بداية الدوام" hint="بالتوقيت المحلي للمدرسة، 24 ساعة">
          <input
            type="time"
            value={shift.start_time}
            onChange={(e) => onChange({ ...shift, start_time: e.target.value })}
            className="mono"
          />
        </Field>
        <Field
          label="عند تكرار الوقت (توقيت صيفي)"
          hint="مطلوب فقط في مناطق تطبّق التوقيت الصيفي"
        >
          <select
            value={shift.ambiguous_time_preference ?? ''}
            onChange={(e) =>
              onChange({
                ...shift,
                ambiguous_time_preference: (e.target.value || null) as 'first' | 'second' | null,
              })
            }
          >
            <option value="">اطلب اختيارًا (يمنع النشر عند التكرار)</option>
            <option value="first">المرة الأولى</option>
            <option value="second">المرة الثانية</option>
          </select>
        </Field>
      </div>

      <Field label="أيام الدوام">
        <div className="pill-list">
          {WEEKDAYS.map((d) => {
            const on = shift.weekdays.includes(d.value);
            return (
              <button
                key={d.value}
                type="button"
                className={on ? 'primary' : ''}
                onClick={() =>
                  onChange({
                    ...shift,
                    weekdays: on
                      ? shift.weekdays.filter((w) => w !== d.value)
                      : [...shift.weekdays, d.value].sort(),
                  })
                }
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </Field>

      <h3 style={{ fontSize: 14, marginTop: 18 }}>تسلسل الحصص والاستراحات</h3>
      <div className="timeline">
        {shift.boundary_rules.map((rule, i) => (
          <div key={i}>
            <BoundaryRow
              rule={rule}
              time={times[i] ?? '—'}
              label={boundaryLabel(shift.periods, i)}
              assets={assets}
              onChange={(next) =>
                onChange({
                  ...shift,
                  boundary_rules: shift.boundary_rules.map((b, x) => (x === i ? next : b)),
                })
              }
            />
            {i < shift.periods.length ? (
              <PeriodRow
                period={shift.periods[i]!}
                onChange={(next) =>
                  setPeriods(shift.periods.map((p, x) => (x === i ? next : p)))
                }
                onRemove={() => setPeriods(shift.periods.filter((_, x) => x !== i))}
              />
            ) : null}
          </div>
        ))}
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        <button onClick={() => addPeriod('lesson')}>إضافة حصة</button>
        <button onClick={() => addPeriod('break')}>إضافة استراحة</button>
      </div>

      <h3 style={{ fontSize: 14, marginTop: 20 }}>أحداث مستقلة (نشيد، تسجيل خاص)</h3>
      {shift.extra_rules.length === 0 ? (
        <Empty title="لا توجد أحداث مستقلة" hint="مثل النشيد قبل بداية الدوام، أو تسجيل مرتبط باستراحة." />
      ) : (
        shift.extra_rules.map((rule, i) => (
          <ExtraRuleEditor
            key={rule.rule_id ?? i}
            rule={rule}
            boundaryCount={shift.periods.length + 1}
            boundaryLabels={shift.boundary_rules.map((_, x) => boundaryLabel(shift.periods, x))}
            assets={assets}
            onChange={(next) =>
              onChange({ ...shift, extra_rules: shift.extra_rules.map((r, x) => (x === i ? next : r)) })
            }
            onRemove={() =>
              onChange({ ...shift, extra_rules: shift.extra_rules.filter((_, x) => x !== i) })
            }
          />
        ))
      )}
      <button
        style={{ marginTop: 10 }}
        onClick={() =>
          onChange({
            ...shift,
            extra_rules: [
              ...shift.extra_rules,
              {
                enabled: true,
                kind: 'custom',
                label: 'حدث جديد',
                asset_id: null,
                max_duration_ms: 30_000,
                truncate: false,
                anchor_type: 'shift_start',
                anchor_boundary_index: null,
                anchor_time: null,
                offset_minutes: 0,
                run_on_holidays: false,
                ambiguous_time_preference: null,
              },
            ],
          })
        }
      >
        إضافة حدث مستقل
      </button>
    </Card>
  );
}

function BoundaryRow({
  rule,
  time,
  label,
  assets,
  onChange,
}: {
  rule: BoundaryRule;
  time: string;
  label: string;
  assets: AudioAsset[];
  onChange: (next: BoundaryRule) => void;
}): JSX.Element {
  const asset = assets.find((a) => a.id === rule.asset_id);
  const tooLong = asset?.duration_ms != null && asset.duration_ms > rule.max_duration_ms;

  return (
    <div className="timeline-row boundary">
      <div className="mono" style={{ fontWeight: 600 }}>{time}</div>
      <div>
        <div style={{ fontSize: 13.5 }}>{rule.label || label}</div>
        <div className="row" style={{ gap: 6, marginTop: 4 }}>
          <select
            aria-label="الملف الصوتي"
            value={rule.asset_id ?? ''}
            onChange={(e) => onChange({ ...rule, asset_id: e.target.value || null })}
            style={{ width: 'auto', minWidth: 180 }}
          >
            <option value="">بلا صوت (حد صامت)</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.original_name} ({formatDuration(a.duration_ms)})
              </option>
            ))}
          </select>
          <input
            aria-label="إزاحة بالدقائق"
            type="number"
            value={rule.offset_minutes}
            onChange={(e) => onChange({ ...rule, offset_minutes: Number(e.target.value) })}
            style={{ width: 80 }}
            title="إزاحة بالدقائق"
          />
          {tooLong ? (
            <label style={{ display: 'flex', gap: 5, alignItems: 'center', margin: 0, color: 'var(--warn)' }}>
              <input
                type="checkbox"
                checked={rule.truncate}
                onChange={(e) => onChange({ ...rule, truncate: e.target.checked })}
              />
              تقصير صريح
            </label>
          ) : null}
        </div>
        {tooLong && !rule.truncate ? (
          <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
            الملف ({formatDuration(asset!.duration_ms)}) أطول من نافذة الحدث (
            {formatDuration(rule.max_duration_ms)}). فعّل التقصير الصريح أو زد النافذة.
          </div>
        ) : null}
      </div>
      <label style={{ display: 'flex', gap: 5, alignItems: 'center', margin: 0 }}>
        <input
          type="checkbox"
          checked={rule.enabled}
          onChange={(e) => onChange({ ...rule, enabled: e.target.checked })}
        />
        مفعّل
      </label>
    </div>
  );
}

function PeriodRow({
  period,
  onChange,
  onRemove,
}: {
  period: Period;
  onChange: (next: Period) => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <div className="timeline-row period">
      <div style={{ fontSize: 12.5 }}>{period.kind === 'lesson' ? 'حصة' : 'استراحة'}</div>
      <div className="row" style={{ gap: 6 }}>
        <input
          aria-label="رقم الفترة"
          type="number"
          min={1}
          value={period.index}
          onChange={(e) => onChange({ ...period, index: Number(e.target.value) })}
          style={{ width: 70 }}
        />
        <input
          aria-label="المدة بالدقائق"
          type="number"
          min={1}
          max={600}
          value={period.duration_minutes}
          onChange={(e) => onChange({ ...period, duration_minutes: Number(e.target.value) })}
          style={{ width: 90 }}
        />
        <span style={{ fontSize: 12.5 }}>دقيقة</span>
      </div>
      <button className="ghost" onClick={onRemove}>
        حذف
      </button>
    </div>
  );
}

function ExtraRuleEditor({
  rule,
  boundaryCount,
  boundaryLabels,
  assets,
  onChange,
  onRemove,
}: {
  rule: ExtraRule;
  boundaryCount: number;
  boundaryLabels: string[];
  assets: AudioAsset[];
  onChange: (next: ExtraRule) => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 10,
        background: 'var(--surface-2)',
      }}
    >
      <div className="grid cols-3">
        <Field label="العنوان">
          <input value={rule.label} onChange={(e) => onChange({ ...rule, label: e.target.value })} />
        </Field>
        <Field label="النوع">
          <select
            value={rule.kind}
            onChange={(e) => onChange({ ...rule, kind: e.target.value as 'anthem' | 'custom' })}
          >
            <option value="custom">حدث خاص</option>
            <option value="anthem">نشيد</option>
          </select>
        </Field>
        <Field label="الملف الصوتي">
          <select
            value={rule.asset_id ?? ''}
            onChange={(e) => onChange({ ...rule, asset_id: e.target.value || null })}
          >
            <option value="">بلا صوت</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.original_name} ({formatDuration(a.duration_ms)})
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid cols-3">
        <Field label="الارتباط" hint="الارتباط بحد يجعل وقت الحدث يتغير مع تغير مدد الفترات">
          <select
            value={rule.anchor_type}
            onChange={(e) =>
              onChange({
                ...rule,
                anchor_type: e.target.value as ExtraRule['anchor_type'],
                anchor_boundary_index: e.target.value === 'boundary' ? 0 : null,
                anchor_time: e.target.value === 'local_time' ? '07:15' : null,
              })
            }
          >
            <option value="shift_start">بداية الدوام + إزاحة</option>
            <option value="boundary">حد محدد + إزاحة</option>
            <option value="local_time">وقت محلي ثابت</option>
          </select>
        </Field>

        {rule.anchor_type === 'boundary' ? (
          <Field label="الحد المرتبط">
            <select
              value={rule.anchor_boundary_index ?? 0}
              onChange={(e) => onChange({ ...rule, anchor_boundary_index: Number(e.target.value) })}
            >
              {Array.from({ length: boundaryCount }, (_, i) => (
                <option key={i} value={i}>
                  {i}. {boundaryLabels[i] ?? `حد ${i}`}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {rule.anchor_type === 'local_time' ? (
          <Field label="الوقت المحلي">
            <input
              type="time"
              className="mono"
              value={rule.anchor_time ?? '07:15'}
              onChange={(e) => onChange({ ...rule, anchor_time: e.target.value })}
            />
          </Field>
        ) : null}

        {rule.anchor_type !== 'local_time' ? (
          <Field label="الإزاحة بالدقائق" hint="سالب = قبل، موجب = بعد">
            <input
              type="number"
              value={rule.offset_minutes}
              onChange={(e) => onChange({ ...rule, offset_minutes: Number(e.target.value) })}
            />
          </Field>
        ) : null}

        <Field label="أقصى مدة تشغيل" hint="بالملي ثانية">
          <input
            type="number"
            min={100}
            max={300_000}
            value={rule.max_duration_ms}
            onChange={(e) => onChange({ ...rule, max_duration_ms: Number(e.target.value) })}
          />
        </Field>
      </div>

      <div className="row">
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
          <input
            type="checkbox"
            checked={rule.enabled}
            onChange={(e) => onChange({ ...rule, enabled: e.target.checked })}
          />
          مفعّل
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
          <input
            type="checkbox"
            checked={rule.truncate}
            onChange={(e) => onChange({ ...rule, truncate: e.target.checked })}
          />
          تقصير صريح عند تجاوز النافذة
        </label>
        {/* §8: a holiday suppresses the day unless an event opts out explicitly. */}
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
          <input
            type="checkbox"
            checked={rule.run_on_holidays}
            onChange={(e) => onChange({ ...rule, run_on_holidays: e.target.checked })}
          />
          يعمل في العطل (استثناء صريح)
        </label>
        <div style={{ flex: 1 }} />
        <button className="ghost" onClick={onRemove}>
          حذف الحدث
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- preview */

function PreviewPanel({
  preview,
  canPublish,
  onPublish,
}: {
  preview: PreviewResult;
  canPublish: boolean;
  onPublish: () => void;
}): JSX.Element {
  return (
    <Card
      title="معاينة النشر"
      subtitle={`${preview.from_date} إلى ${preview.to_date}`}
      actions={
        canPublish ? (
          <button className="primary" disabled={!preview.can_publish} onClick={onPublish}>
            اعتماد ونشر
          </button>
        ) : null
      }
    >
      <div className="grid cols-4" style={{ marginBottom: 14 }}>
        <div className="stat">
          <div className="label">الأجهزة المستهدفة</div>
          <div className="value num">{preview.device_count}</div>
        </div>
        <div className="stat">
          <div className="label">إجمالي الأحداث</div>
          <div className="value num">{preview.total_events}</div>
        </div>
        <div className="stat">
          <div className="label">أجهزة بمشاكل حاجبة</div>
          <div className={`value num ${preview.blocking_device_count ? 'danger' : 'ok'}`}>
            {preview.blocking_device_count}
          </div>
        </div>
        <div className="stat">
          <div className="label">قابل للنشر</div>
          <div className={`value ${preview.can_publish ? 'ok' : 'danger'}`}>
            {preview.can_publish ? 'نعم' : 'لا'}
          </div>
        </div>
      </div>

      {preview.global_problems.length > 0 ? (
        <Notice tone="danger">
          <ul style={{ margin: 0, paddingInlineStart: 18 }}>
            {preview.global_problems.map((p, i) => (
              <li key={i}>
                <strong>{PROBLEM_LABELS[p.code] ?? p.code}:</strong> {p.message}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}

      {preview.devices.length === 0 ? (
        <Empty title="لا توجد أجهزة ضمن النطاق" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>الجهاز</th>
                <th>المدرسة</th>
                <th>مصدر الإسناد</th>
                <th>الأحداث</th>
                <th>الملفات</th>
                <th>أول الأحداث</th>
                <th>الأيام المعطّلة</th>
                <th>المشاكل</th>
              </tr>
            </thead>
            <tbody>
              {preview.devices.map((d) => (
                <tr key={d.device_id}>
                  <td>{d.device_name}</td>
                  <td>{d.school_name}</td>
                  <td>
                    <Badge tone="muted">{d.assignment_source}</Badge>
                  </td>
                  <td className="num">{d.event_count}</td>
                  <td className="num">
                    {d.asset_count} ({Math.round(d.required_storage_bytes / 1024)} ك.ب)
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {d.first_events.slice(0, 3).map((e, i) => (
                      <div key={i}>
                        <span className="mono">{formatDateTime(e.at, d.timezone)}</span> —{' '}
                        {EVENT_KIND_LABELS[e.kind] ?? e.kind}
                      </div>
                    ))}
                  </td>
                  <td className="num">{d.suppressed_dates.length}</td>
                  <td style={{ fontSize: 12 }}>
                    {d.problems.length === 0 ? (
                      <Badge tone="ok" icon="✔">
                        لا يوجد
                      </Badge>
                    ) : (
                      d.problems.map((p, i) => (
                        <div key={i} style={{ color: p.severity === 'error' ? 'var(--danger)' : 'var(--warn)' }}>
                          {PROBLEM_LABELS[p.code] ?? p.code}: {p.message}
                        </div>
                      ))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function PublishDialog({
  preview,
  busy,
  onCancel,
  onConfirm,
}: {
  preview: PreviewResult;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}): JSX.Element {
  const [note, setNote] = useState('');
  return (
    <Modal
      title="اعتماد النشر"
      onClose={onCancel}
      footer={
        <>
          <button onClick={onCancel} disabled={busy}>
            إلغاء
          </button>
          <button className="primary" onClick={() => onConfirm(note)} disabled={busy}>
            {busy ? 'جارٍ النشر…' : 'اعتماد ونشر'}
          </button>
        </>
      }
    >
      <Notice tone="warn">
        سيُنشأ إصدار غير قابل للتعديل يُطبَّق على{' '}
        <strong className="num">{preview.device_count}</strong> جهازًا، بمجموع{' '}
        <strong className="num">{preview.total_events}</strong> حدثًا حتى{' '}
        <span className="mono">{preview.to_date}</span>. التعديل يسري على الأحداث المستقبلية بعد
        التفعيل، ولا يقاطع صوتًا عاديًا بدأ بالفعل.
      </Notice>
      <Field label="ملاحظة النشر (تُسجَّل في التدقيق)">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="سبب التعديل" />
      </Field>
    </Modal>
  );
}
